<script setup lang="ts">
/**
 * On-site renderer for CDP EXPERIENCES (Фаза 2) — the common in-app pipeline the
 * game-host uses, but for popups & surveys on the storefront. Reads the current
 * renderable from the cdp store (which polls `/e/v1/pending`), draws a popup
 * (title/body/CTA/promo code) or a survey (rating + optional comment/extra
 * questions), and reports impression/click/dismiss/submitted telemetry via the
 * store. Games/chat/recs experiences are skipped (handled by their own hosts).
 *
 * The storefront is light-only by design (see style.css), so this matches the
 * vd-* design system rather than theming for dark.
 */
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useCdpStore } from "../stores/cdp";
import { surveyScoreOptions } from "../cdp/experience-view";

const cdp = useCdpStore();
const router = useRouter();
const current = computed(() => cdp.current);

const popup = computed(() => (current.value?.kind === "popup" ? current.value : null));
const survey = computed(() => (current.value?.kind === "survey" ? current.value : null));
// Попап из конструктора блоков: сервер уже отрисовал и санитизировал HTML. Кладём его в iframe
// БЕЗ скриптов (sandbox) — вторая линия защиты и изоляция стилей от витрины. Раньше такие попапы
// витрина молча пропускала (баг-лист Насти п.1).
const htmlPopup = computed(() => (current.value?.kind === "html" ? current.value : null));
const isPreview = computed(() => {
  const c = current.value;
  return !!c && c.kind !== "survey" && c.preview === true;
});
const isSlideIn = computed(() => current.value?.kind !== "survey" && current.value?.presentation === "slide_in");
const frameHeight = ref(220);
const assistantNotice = ref("");

function htmlDocument(html: string): string {
  return `<!doctype html><html lang="ru"><head><meta charset="utf-8"><base target="_top">` +
    `<style>html,body{margin:0;padding:0;background:transparent;font-family:system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif;}` +
    `img{max-width:100%;height:auto}a{cursor:pointer}</style></head><body>${html}</body></html>`;
}

function isSafeNavigation(url: string): boolean {
  try {
    const target = new URL(url, window.location.origin);
    return ["http:", "https:", "mailto:", "tel:"].includes(target.protocol);
  } catch {
    return false;
  }
}

function onFrameLoad(e: Event): void {
  const frame = e.target as HTMLIFrameElement;
  const doc = frame.contentDocument;
  if (!doc) return;
  const maxHeight = Math.round(window.innerHeight * 0.8);
  // Высота по содержимому body: documentElement.scrollHeight не бывает меньше самого окна iframe,
  // и под коротким текстом оставалась пустота.
  frameHeight.value = Math.min(Math.max(Math.ceil(doc.body.getBoundingClientRect().height), 60), maxHeight);
  // Скрипты внутри отключены; слушатель — со стороны витрины (same-origin srcdoc).
  doc.addEventListener("click", (ev) => {
    const link = (ev.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!link) return;
    ev.preventDefault();
    const url = link.getAttribute("href") || "";
    void cdp.clickCurrentCta();
    if (url === "wings://open-chat") {
      void openAssistantFromInvitation();
      return;
    }
    cdp.dismissCurrent();
    // Защита в глубину (HTML уже санитизирован сервером): переход только по http(s)/mailto/tel или
    // по относительному адресу — никакого javascript:/data: в контексте витрины.
    if (url && url !== "#" && isSafeNavigation(url)) navigateFromCta(url);
  });
}

// ── survey local state ──────────────────────────────────────────────────────
const score = ref<number | null>(null);
const comment = ref("");
const extra = ref<Record<string, unknown>>({});
const submitting = ref(false);
const thanksTitle = ref<string | null>(null);

const scoreOptions = computed(() =>
  survey.value ? surveyScoreOptions(survey.value.scaleType) : [],
);

// Reset the form whenever a different experience becomes current.
watch(
  () => current.value?.experienceId,
  () => {
    frameHeight.value = 220;
    assistantNotice.value = "";
    score.value = null;
    comment.value = "";
    extra.value = {};
    submitting.value = false;
    thanksTitle.value = null;
  },
);

function accent(): string {
  return current.value?.primary || "var(--vd-brand)";
}

function onClose(): void {
  cdp.dismissCurrent();
}

async function openAssistantFromInvitation(): Promise<void> {
  const invitation = cdp.current;
  const stillCurrent = () => cdp.current === invitation || (
    !!invitation && !!cdp.current &&
    cdp.current.experienceId === invitation.experienceId &&
    cdp.current.assignmentId === invitation.assignmentId
  );
  assistantNotice.value = "";
  try {
    const opened = await cdp.openChatFromCurrent();
    if (!stillCurrent()) return;
    if (opened) cdp.dismissCurrent();
    else assistantNotice.value = "Помощник временно недоступен. Повторите попытку или выберите рецепт в книге.";
  } catch {
    if (!stillCurrent()) return;
    assistantNotice.value = "Помощник временно недоступен. Повторите попытку или выберите рецепт в книге.";
  }
}

async function onPopupCta(): Promise<void> {
  const p = popup.value;
  void cdp.clickCurrentCta();
  if (p?.cta?.action === "open_chat") {
    await openAssistantFromInvitation();
    return;
  }
  const url = p?.cta?.url ?? null;
  cdp.dismissCurrent();
  if (url) navigateFromCta(url);
}

/**
 * Follow a popup CTA without breaking the PWA. A **same-origin** target (a
 * relative path like `/checkout?promo=…`, or an absolute URL on our own origin)
 * is navigated via the Vue Router, so an iOS/Android PWA or in-app webview keeps
 * running the SPA instead of spawning an in-app browser (a dead-end window), and
 * query params (e.g. the pre-applied promo) survive. Only a genuinely
 * cross-origin URL falls back to a hard `window.location` navigation.
 */
function navigateFromCta(url: string): void {
  try {
    const target = new URL(url, window.location.origin);
    if (target.origin === window.location.origin) {
      // Route-location object → query is handed over already-decoded (e.g.
      // `promo: "ВКУСНО10"`), and the router re-encodes it correctly.
      void router.push({
        path: target.pathname,
        query: Object.fromEntries(target.searchParams),
        hash: target.hash,
      });
      return;
    }
  } catch {
    // Unparseable string that still looks like an in-app path → route it.
    if (url.startsWith("/")) {
      void router.push(url);
      return;
    }
  }
  window.location.href = url;
}

function setExtra(id: string, value: unknown): void {
  extra.value = { ...extra.value, [id]: value };
}
function toggleMulti(id: string, option: string): void {
  const cur = Array.isArray(extra.value[id]) ? (extra.value[id] as string[]) : [];
  const next = cur.includes(option) ? cur.filter((o) => o !== option) : [...cur, option];
  setExtra(id, next);
}
function isPicked(id: string, option: string): boolean {
  const v = extra.value[id];
  return Array.isArray(v) ? v.includes(option) : v === option;
}

const canSubmit = computed(() => {
  const s = survey.value;
  if (!s || score.value == null || submitting.value) return false;
  // required extra questions must be answered
  return s.extraQuestions.every((q) => {
    if (!q.required) return true;
    const v = extra.value[q.id];
    if (q.type === "multi_choice") return Array.isArray(v) && v.length > 0;
    return v != null && String(v).length > 0;
  });
});

async function onSubmitSurvey(): Promise<void> {
  const s = survey.value;
  if (!s || score.value == null) return;
  submitting.value = true;
  const res = await cdp.submitSurvey(s, {
    score: score.value,
    comment: comment.value.trim() || null,
    extra_answers: Object.keys(extra.value).length ? extra.value : null,
  });
  submitting.value = false;
  if (res.ok) {
    thanksTitle.value = s.thanksTitle;
    window.setTimeout(() => cdp.dismissCurrent(), 2200);
  }
}
</script>

<template>
  <div
    v-if="current"
    class="cdp-exp-backdrop"
    :class="{ 'is-slide-in': isSlideIn }"
    @click.self="onClose"
  >
    <!-- ── POPUP ─────────────────────────────────────────────────────── -->
    <div
      v-if="popup"
      class="cdp-exp-card"
      role="dialog"
      :aria-modal="isSlideIn ? 'false' : 'true'"
      :style="{ '--exp-accent': accent() }"
    >
      <button type="button" class="cdp-exp-close" aria-label="Закрыть" @click="onClose">×</button>
      <span v-if="isPreview" class="cdp-exp-preview" data-test="cdp-exp-preview">Тестовый показ</span>
      <div v-if="popup.image" class="cdp-exp-emoji">{{ popup.image }}</div>
      <h2 v-if="popup.title" class="cdp-exp-title">{{ popup.title }}</h2>
      <p v-if="popup.body" class="cdp-exp-body">{{ popup.body }}</p>
      <div v-if="popup.code" class="cdp-exp-code">
        Промокод: <strong>{{ popup.code }}</strong>
      </div>
      <button
        v-if="popup.cta"
        type="button"
        class="cdp-exp-cta"
        @click="onPopupCta"
      >
        {{ popup.cta.label }}
      </button>
      <p v-if="assistantNotice" role="status" class="cdp-exp-body">{{ assistantNotice }}</p>
    </div>

    <!-- ── ПОПАП ИЗ БЛОКОВ (готовый HTML от сервера) ─────────────────── -->
    <div
      v-else-if="htmlPopup"
      class="cdp-exp-card is-html"
      role="dialog"
      :aria-modal="isSlideIn ? 'false' : 'true'"
      data-test="cdp-exp-html"
    >
      <button type="button" class="cdp-exp-close" aria-label="Закрыть" @click="onClose">×</button>
      <span v-if="isPreview" class="cdp-exp-preview" data-test="cdp-exp-preview">Тестовый показ</span>
      <iframe
        class="cdp-exp-frame"
        title="Сообщение"
        :srcdoc="htmlDocument(htmlPopup.html)"
        sandbox="allow-same-origin allow-popups allow-top-navigation-by-user-activation"
        :style="{ height: frameHeight + 'px' }"
        @load="onFrameLoad"
      />
      <p v-if="assistantNotice" role="status" class="cdp-exp-body">{{ assistantNotice }}</p>
    </div>

    <!-- ── SURVEY ────────────────────────────────────────────────────── -->
    <div
      v-else-if="survey"
      class="cdp-exp-card"
      role="dialog"
      :aria-modal="isSlideIn ? 'false' : 'true'"
      :style="{ '--exp-accent': accent() }"
    >
      <button type="button" class="cdp-exp-close" aria-label="Закрыть" @click="onClose">×</button>

      <template v-if="!thanksTitle">
        <h2 class="cdp-exp-title">{{ survey.name || 'Оцените' }}</h2>
        <p class="cdp-exp-body">{{ survey.question }}</p>

        <div class="cdp-exp-scale" :class="{ 'is-thumbs': survey.scaleType === 'thumbs' }">
          <button
            v-for="opt in scoreOptions"
            :key="opt.value"
            type="button"
            class="cdp-exp-score"
            :class="{ 'is-active': score === opt.value }"
            @click="score = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>

        <!-- extra questions -->
        <div v-for="q in survey.extraQuestions" :key="q.id" class="cdp-exp-extra">
          <p class="cdp-exp-extra-label">{{ q.text }}<span v-if="q.required" aria-hidden="true"> *</span></p>

          <div v-if="q.type === 'single_choice'" class="cdp-exp-choices">
            <button
              v-for="o in q.options"
              :key="o"
              type="button"
              class="cdp-exp-choice"
              :class="{ 'is-active': isPicked(q.id, o) }"
              @click="setExtra(q.id, o)"
            >{{ o }}</button>
          </div>

          <div v-else-if="q.type === 'multi_choice'" class="cdp-exp-choices">
            <button
              v-for="o in q.options"
              :key="o"
              type="button"
              class="cdp-exp-choice"
              :class="{ 'is-active': isPicked(q.id, o) }"
              @click="toggleMulti(q.id, o)"
            >{{ o }}</button>
          </div>

          <div v-else-if="q.type === 'stars'" class="cdp-exp-scale">
            <button
              v-for="n in 5"
              :key="n"
              type="button"
              class="cdp-exp-score"
              :class="{ 'is-active': extra[q.id] === n }"
              @click="setExtra(q.id, n)"
            >{{ n }}</button>
          </div>

          <input
            v-else
            :value="(extra[q.id] as string) ?? ''"
            type="text"
            class="cdp-exp-input"
            :placeholder="'Ваш ответ'"
            @input="setExtra(q.id, ($event.target as HTMLInputElement).value)"
          />
        </div>

        <textarea
          v-if="survey.askComment"
          v-model="comment"
          class="cdp-exp-input cdp-exp-textarea"
          placeholder="Комментарий (по желанию)"
          rows="2"
        />

        <button
          type="button"
          class="cdp-exp-cta"
          :disabled="!canSubmit"
          @click="onSubmitSurvey"
        >
          {{ submitting ? 'Отправляем…' : 'Отправить' }}
        </button>
      </template>

      <template v-else>
        <div class="cdp-exp-emoji">✅</div>
        <h2 class="cdp-exp-title">{{ thanksTitle }}</h2>
      </template>
    </div>
  </div>
</template>

<style scoped>
.cdp-exp-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(20, 30, 22, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  padding: 16px;
}
.cdp-exp-backdrop.is-slide-in {
  inset: auto 24px 160px auto;
  padding: 0;
  width: min(340px, calc(100vw - 32px));
  background: transparent;
  pointer-events: none;
}
.is-slide-in .cdp-exp-card {
  pointer-events: auto;
  padding: 24px 20px;
  text-align: left;
  border: 1px solid var(--vd-line);
}
body:has([data-sticky-cta]) .cdp-exp-backdrop.is-slide-in { bottom: 220px; }
@media (max-width: 640px) {
  body:has([data-sticky-cta]) .cdp-exp-backdrop.is-slide-in { bottom: calc(var(--vd-nav-h, 0px) + 220px); }
  .cdp-exp-backdrop.is-slide-in { right: 12px; bottom: 220px; }
}
.cdp-exp-card {
  position: relative;
  background: var(--vd-card, #fff);
  border-radius: 16px;
  padding: 28px 24px;
  max-width: 380px;
  width: 100%;
  text-align: center;
  box-shadow: 0 12px 32px rgba(20, 30, 22, 0.25);
}
.cdp-exp-card.is-html {
  padding: 0;
  max-width: 440px;
  text-align: left;
  overflow: hidden;
}
.cdp-exp-frame {
  display: block;
  width: 100%;
  border: 0;
  background: transparent;
}
.cdp-exp-preview {
  position: absolute;
  top: 10px;
  left: 12px;
  z-index: 1;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff4d6;
  color: #7a5200;
  font-size: 12px;
  font-weight: 600;
}
.cdp-exp-close {
  z-index: 1;
  position: absolute;
  top: 8px;
  right: 12px;
  background: transparent;
  border: 0;
  font-size: 26px;
  line-height: 1;
  color: var(--vd-muted, #9aa39c);
  cursor: pointer;
}
.cdp-exp-emoji {
  font-size: 48px;
  margin-bottom: 10px;
}
.cdp-exp-title {
  margin: 0 0 8px;
  color: var(--exp-accent, var(--vd-brand));
  font-size: 21px;
  font-weight: 700;
}
.cdp-exp-body {
  margin: 0 0 18px;
  color: var(--vd-ink-2, #37433a);
  line-height: 1.5;
  font-size: 15px;
}
.cdp-exp-code {
  margin: 0 0 16px;
  padding: 8px 12px;
  background: var(--vd-brand-tint, #e9f5ec);
  border-radius: 10px;
  color: var(--vd-brand-ink, #2e7d46);
  font-size: 15px;
}
.cdp-exp-scale {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.cdp-exp-scale.is-thumbs .cdp-exp-score {
  font-size: 24px;
  width: 56px;
  height: 48px;
}
.cdp-exp-score {
  min-width: 40px;
  height: 40px;
  padding: 0 8px;
  border: 1px solid var(--vd-line, #eef0ec);
  border-radius: 10px;
  background: var(--vd-tint, #f2f4f0);
  color: var(--vd-ink, #19251c);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s ease;
}
.cdp-exp-score.is-active {
  background: var(--exp-accent, var(--vd-brand));
  color: #fff;
  border-color: var(--exp-accent, var(--vd-brand));
}
.cdp-exp-extra {
  text-align: left;
  margin-bottom: 14px;
}
.cdp-exp-extra-label {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--vd-ink, #19251c);
}
.cdp-exp-choices {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.cdp-exp-choice {
  padding: 8px 12px;
  border: 1px solid var(--vd-line, #eef0ec);
  border-radius: 999px;
  background: var(--vd-tint, #f2f4f0);
  color: var(--vd-ink-2, #37433a);
  font-size: 13px;
  cursor: pointer;
}
.cdp-exp-choice.is-active {
  background: var(--exp-accent, var(--vd-brand));
  color: #fff;
  border-color: var(--exp-accent, var(--vd-brand));
}
.cdp-exp-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--vd-line, #eef0ec);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  color: var(--vd-ink, #19251c);
  background: #fff;
  margin-bottom: 12px;
}
.cdp-exp-textarea {
  resize: vertical;
}
.cdp-exp-cta {
  width: 100%;
  background: var(--exp-accent, var(--vd-brand));
  color: #fff;
  border: 0;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}
.cdp-exp-cta:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cdp-exp-cta:not(:disabled):hover {
  filter: brightness(0.96);
}
</style>
